/**
 * The four-to-six places you could go next.
 *
 * ## The column is a set of slots, not a list that grows
 *
 * Suggestions stream in one at a time over NDJSON, which is by design — the first idea
 * should be readable while the fifth is still being written. But a column that renders
 * only what has arrived re-lays itself out on every arrival: the rows are `flex: 1 1 0`
 * inside a fixed-height list, so one card is a full-height card, two cards are half-height
 * cards, and the whole set visibly jolts four times before it settles. On a slow model
 * that is the most conspicuous thing on the screen.
 *
 * So the column renders **every slot up front** and fills them in place. Row geometry is
 * fixed from the first frame, each arriving suggestion replaces the skeleton in its own
 * slot and nothing else on screen moves, resizes or re-animates. The cards already have
 * stable keys (the suggestion id); the skeletons key off their slot index, so the ones
 * still waiting keep their DOM nodes as their neighbours fill.
 *
 * ## How many slots
 *
 * Nobody down here knows what the request asked for — `branchCount` is an effects-layer
 * concern and `BranchSet` does not carry it. The best available evidence is the previous
 * node's set: every request in a session is built with the same `branchCount`, so the set
 * that is on its way out is the same size as the one arriving. With no previous set (the
 * first chord of a session) it falls back to `DEFAULT_BRANCH_COUNT`, which is what the
 * effects layer actually asks for. Either way the estimate only has to be right about
 * *geometry*; a set that overruns it grows once, at the end, rather than on every arrival.
 *
 * ## What happens while a set is loading
 *
 * Nothing is removed. The previous node's suggestions stay exactly where they were, faded,
 * with skeletons in front of them — so the column never becomes an empty rectangle, and the
 * eye never has to re-find anything. The stale layer goes when the first new card lands,
 * which is the designed guarantee, and the current chord stays fully interactive
 * throughout: waiting on the network is the app's problem and not the player's.
 *
 * ## What happens when a set fails
 *
 * Also nothing dramatic. One quiet line, a retry glyph, and the previous ideas still
 * faded in place. The node itself joined the path normally — recognition is local and
 * kept working — so there is nothing here that deserves an error state.
 */

import { AnimatePresence, motion } from 'framer-motion';
import {
  DEFAULT_BRANCH_COUNT,
  MAX_BRANCH_COUNT,
  MIN_BRANCH_COUNT,
  type BranchSet,
  type BranchSuggestion,
  type CanonicalChord,
  type KeyEstimate,
} from '@hc/shared';
import type { BranchPhase } from '../system/sessionPhase.js';
import type { ExplanationFetcher } from '../system/sessionView.js';
import { BranchCard, BranchCardSkeleton } from './BranchCard.js';
import { BranchFan } from './PathConnector.js';
import { CALM_FADE, cardTransition } from './motion.js';

/** A strand of the fan with no card on the end of it yet. Neutral, never a guess. */
const PENDING_ADVENTUROUSNESS = 0.5;

/** True once the set is everything it is ever going to be. */
function isComplete(set: BranchSet | null): boolean {
  return set !== null && (set.status === 'ready' || set.status === 'fallback');
}

/**
 * How many rows the column holds while a set is arriving.
 *
 * A complete set is exactly what it says it is. An incomplete one is sized against the
 * previous node's set — see the note at the top of this file — clamped to the brief's
 * 4..6, and never smaller than what has already arrived.
 */
export function branchSlotCount(set: BranchSet | null, previous: BranchSet | null): number {
  const arrived = set?.suggestions.length ?? 0;
  if (isComplete(set)) return arrived;
  const expected = previous?.suggestions.length || DEFAULT_BRANCH_COUNT;
  return Math.max(arrived, Math.min(MAX_BRANCH_COUNT, Math.max(MIN_BRANCH_COUNT, expected)));
}

/**
 * The two-stage card, as one object, at column scope.
 *
 * `expandedId` rather than a flag per card: only one card is open at a time, so the state
 * is a single id and "expanding one collapses the previous" is a property of the shape
 * rather than a rule somebody has to remember to enforce. The id survives suggestions
 * streaming in around it, which is the guarantee round two bought and this must not spend.
 */
export interface BranchExpansion {
  expandedId: string | null;
  onToggle: (suggestion: BranchSuggestion) => void;
  /** A phone. Decides how a shut card is drawn and how the column makes room for an open
   * one — never whether a tap opens rather than takes, which it always does. */
  stacked: boolean;
  fetchExplanation: ExplanationFetcher | undefined;
  keyEstimate: KeyEstimate;
  /** `view.previewChord`: hear an option without taking it. Absent at the piano. */
  onPreview?: ((chord: CanonicalChord) => void) | undefined;
}

/**
 * ## What the rest of the set does while one card is open
 *
 * Two answers, and the difference between them is space rather than principle.
 *
 * **On a phone the open card is the column.** The others are `hidden` and the column
 * stops scrolling — the card takes the whole branch area and does its own scrolling
 * inside itself (see `BranchCard`). There is no room for anything else, and a set of
 * half-covered cards under an opaque one reads as a rendering fault.
 *
 * **Wider they stay, receded.** An iPad or a desktop has the height to show four dimmed
 * alternatives beside the one being read, and seeing them is most of the value of a
 * branch column: the question "why this one" is really "why this one *rather than those*".
 * They are squeezed rather than removed — same rows, same order, same left edge, nothing
 * enters or leaves — so the column resizes in place instead of being rebuilt around the
 * choice. They stay tappable, and tapping one is how you move from reading about this
 * option to reading about that one.
 *
 * Two rules hold either way and both are load-bearing:
 *
 *  - **Hidden or dimmed, never unmounted.** The slot system exists so that a card on
 *    screen keeps its DOM node while the rest of the set streams in; throwing four of them
 *    away and rebuilding them on close would spend exactly that, and would re-mount the
 *    hooks that hold their fetched paragraphs.
 *  - **Only if the open card is actually here.** `expandedId` is a suggestion id held by
 *    the page; a set that has just been replaced does not contain it, and receding every
 *    card because of an id belonging to the previous chord would grey out a live column.
 */
function focusedOn(
  expansion: BranchExpansion | undefined,
  set: readonly BranchSuggestion[],
): string | null {
  const id = expansion?.expandedId ?? null;
  return id !== null && set.some((suggestion) => suggestion.id === id) ? id : null;
}

export interface BranchColumnProps {
  phase: BranchPhase;
  branchSet: BranchSet | null;
  previousBranchSet: BranchSet | null;
  /** Respells a suggestion for the current key. Injected so it can be memoised upstream. */
  chordFor: (suggestion: BranchSuggestion) => CanonicalChord;
  calm: boolean;
  onRetry: () => void;
  /**
   * Present only On the Go. The stale layer never gets it: those cards belong to a node
   * the player has already left, and they are `pointer-events: none` for that reason.
   */
  onSelect?: (suggestion: BranchSuggestion) => void;
  /**
   * The two stages, at every width. The stale layer never gets it, and for the same
   * reason — nothing about a set already left behind is worth expanding.
   */
  expansion?: BranchExpansion;
}

function StaleLayer({
  set,
  chordFor,
  calm,
}: {
  set: BranchSet;
  chordFor: (suggestion: BranchSuggestion) => CanonicalChord;
  calm: boolean;
}): React.JSX.Element {
  return (
    <div className="hc-branches__layer">
      <BranchFan adventurousness={set.suggestions.map((s) => s.adventurousness)} dimmed />
      <ul className="hc-branches__list hc-branches__list--stale" data-testid="hc-stale-branches">
        {set.suggestions.map((suggestion, index) => (
          <BranchCard
            key={suggestion.id}
            suggestion={suggestion}
            chord={chordFor(suggestion)}
            index={index}
            calm={calm}
            stale
          />
        ))}
      </ul>
    </div>
  );
}

export function BranchColumn({
  phase,
  branchSet,
  previousBranchSet,
  chordFor,
  calm,
  onRetry,
  onSelect,
  expansion,
}: BranchColumnProps): React.JSX.Element {
  const suggestions = branchSet?.suggestions ?? [];
  const showStale =
    (phase === 'loading' || phase === 'unavailable') &&
    previousBranchSet !== null &&
    previousBranchSet.suggestions.length > 0;

  // One layer for the whole life of a set, from the first empty slot to the last card.
  // Splitting "loading" and "visible" into two layers is what used to make the first
  // arrival replace five skeletons with a single full-height card.
  const filling = phase === 'loading' || phase === 'visible';
  const slots = branchSlotCount(branchSet, previousBranchSet);
  const streaming = !isComplete(branchSet);
  /** The card being read, or null. */
  const focusedId = focusedOn(expansion, suggestions);
  /** A phone, where a shut card is two lines and an open one is the whole column. */
  const stacked = expansion?.stacked === true;
  const strands = Array.from(
    { length: slots },
    (_, index) => suggestions[index]?.adventurousness ?? PENDING_ADVENTUROUSNESS,
  );

  return (
    <div
      className={`hc-branches${stacked ? ' hc-branches--stacked' : ''}${
        focusedId ? ' hc-branches--focused' : ''
      }`}
      data-testid="hc-branch-column"
      data-phase={phase}
      // The column's rows are equal-height and content-height respectively, and a phone is
      // the only place they are the second. The class is what the stylesheet keys the
      // difference off — see `graph.css` — rather than a second media query that could
      // drift from `NARROW_LAYOUT_PX`.
      data-stacked={stacked ? 'true' : undefined}
      // The other half of that: with a card open, a phone column is a single full-height
      // row and stops scrolling, and a wider one gives that row most of its height and
      // recedes the rest. Either way the card is what scrolls.
      data-focused={focusedId ?? undefined}
    >
      {showStale && previousBranchSet ? (
        <StaleLayer set={previousBranchSet} chordFor={chordFor} calm={calm} />
      ) : null}

      {/*
        The whole set is the animated unit, not the individual cards. Two reasons, and the
        second is the load-bearing one:
          1. `AnimatePresence` exits reliably when its direct child is a `motion` element —
             per-card exits through a wrapper component never complete under `popLayout`,
             because it needs a ref on the child it is measuring.
          2. Both lists sit in the same grid cell (`.hc-branches` is a one-cell grid), so
             the outgoing set overlays the incoming one instead of stacking below it. Per
             card, the column would double in height for the length of the transition,
             which is a visible lurch at exactly the moment the player is looking at it.
        The per-card `layoutId` still does the morph: the card being left behind and the
        current node it became share an identity across the two lists.
      */}
      <AnimatePresence initial={false}>
        {filling && branchSet ? (
          <motion.div
            key={branchSet.forNodeId}
            className="hc-branches__layer"
            initial={{ opacity: calm ? 0 : 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={calm ? CALM_FADE : { duration: 0.18, ease: 'easeOut' }}
          >
            {/* Dimmed only while the column is entirely holes; from the first card the
                fan is the real thing, and its geometry never changes under it. */}
            <BranchFan adventurousness={strands} dimmed={suggestions.length === 0} />
            <ul
              className="hc-branches__list"
              aria-label="Where you could go next"
              aria-busy={streaming ? true : undefined}
            >
              {strands.map((_, index) => {
                const suggestion = suggestions[index];
                return suggestion ? (
                  <BranchCard
                    key={suggestion.id}
                    suggestion={suggestion}
                    chord={chordFor(suggestion)}
                    index={index}
                    calm={calm}
                    // A card that lands in a slot of its own crossfades into it. The
                    // slide-and-stagger belongs to a whole set appearing at once, and
                    // replaying it per arrival is the jitter this file is about.
                    streaming={streaming}
                    // Out of the way while another card is open, and still mounted either
                    // way. Off the screen on a phone, where the open card is the column;
                    // dimmed and squeezed wherever there is room to keep it.
                    hidden={stacked && focusedId !== null && suggestion.id !== focusedId}
                    receded={!stacked && focusedId !== null && suggestion.id !== focusedId}
                    {...(onSelect ? { onSelect } : {})}
                    {...(expansion
                      ? {
                          expansion: {
                            expanded: expansion.expandedId === suggestion.id,
                            onToggle: expansion.onToggle,
                            stacked: expansion.stacked,
                            fetchExplanation: expansion.fetchExplanation,
                            keyEstimate: expansion.keyEstimate,
                            ...(expansion.onPreview ? { onPreview: expansion.onPreview } : {}),
                          },
                        }
                      : {})}
                  />
                ) : (
                  <BranchCardSkeleton
                    key={`hc-slot-${index}`}
                    index={index}
                    calm={calm}
                    // A hole is a promise about geometry, and on a phone with a card open
                    // there is no geometry to promise: the open card is the whole column.
                    // Wider the promise still holds — the slot is simply one of the rows
                    // that made room — so the hole stays and fills in place as it always
                    // did, which is what keeps an arrival from disturbing an open card.
                    hidden={stacked && focusedId !== null}
                  />
                );
              })}
            </ul>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {phase === 'unavailable' ? (
        <motion.div
          className="hc-branches__notice"
          data-testid="hc-branches-unavailable"
          // Transform-only entry: this line is the only explanation the player gets for an
          // empty branch column, so it must not depend on an animation having run.
          initial={calm ? false : { y: -6 }}
          animate={{ y: 0 }}
          transition={calm ? CALM_FADE : cardTransition(0, false)}
        >
          <span className="hc-branches__notice-text">ideas unavailable for this chord</span>
          <button
            type="button"
            className="hc-retry"
            onClick={onRetry}
            aria-label="Try again for ideas"
            title="Try again"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M20 12a8 8 0 1 1-2.34-5.66"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M20 4v5h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </motion.div>
      ) : null}

      {branchSet?.status === 'fallback' && phase === 'visible' ? (
        <p className="hc-branches__footnote">ideas from theory, not from the companion</p>
      ) : null}
    </div>
  );
}
